import type { SlideFields } from "@/types/Slide";
import type { GrammarResponseSlideData } from "@ai-teach/types";
import { useState } from "react";
import { MdMusicNote, MdPlayArrow, MdVideocam } from "react-icons/md";

interface GrammarResponseThumbnailProps {
  slide: SlideFields | null;
}

function formatTimestamp(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const gradingModeLabel: Record<string, string> = {
  strictMode: "Strict",
  contextMode: "Context",
};

export function GrammarResponseThumbnail({ slide }: GrammarResponseThumbnailProps) {
  const responseSlideData = slide?.slideData as GrammarResponseSlideData;
  const checkpoints = responseSlideData.checkpoints ?? [];
  const isAudio = responseSlideData.mediaType === "audio";
  const videoSrc = responseSlideData.video?.src ?? null;
  const audioSrc = responseSlideData.audio?.src ?? null;
  const filename = isAudio
    ? responseSlideData.audio?.filename || "Audio Response"
    : responseSlideData.video?.filename || "Video Response";

  const [posterFailed, setPosterFailed] = useState(false);

  return (
    <div className="bg-orange-50 h-full flex flex-col px-2 py-1.5 gap-1.5 overflow-hidden">
      {/* Media preview */}
      <div className="relative shrink-0 h-28 rounded-lg overflow-hidden bg-neutral-900 flex items-center justify-center shadow-sm">
        {!isAudio && videoSrc && !posterFailed ? (
          <>
            <video
              src={`${videoSrc}#t=0.1`}
              muted
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover"
              onError={() => setPosterFailed(true)}
            />
            <div className="relative w-6 h-6 rounded-full bg-black/50 flex items-center justify-center">
              <MdPlayArrow className="text-white" style={{ fontSize: "13px" }} />
            </div>
          </>
        ) : isAudio && audioSrc ? (
          <MdMusicNote className="text-white/80" style={{ fontSize: "22px" }} />
        ) : (
          <div className="flex flex-col items-center gap-0.5 text-white/40">
            <MdVideocam style={{ fontSize: "20px" }} />
            <span className="text-[7px] font-medium">{isAudio ? "No audio" : "No video"}</span>
          </div>
        )}
        <span className="absolute bottom-0 inset-x-0 px-1.5 py-0.5 bg-black/45 text-white text-[7px] font-medium truncate">
          {filename}
        </span>
      </div>

      {/* Checkpoints */}
      {checkpoints.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <span className="text-[9px] font-medium text-orange-300">No checkpoints</span>
        </div>
      ) : (
        <div className="flex-1 flex flex-col gap-1 overflow-hidden">
          {checkpoints.map((cp, index) => (
            <div
              key={`point-${index + 1}`}
              className="bg-white rounded-lg overflow-hidden flex shadow-sm shrink-0"
            >
              <div className="bg-orange-300 flex items-center justify-center px-1.5 shrink-0">
                <span
                  className="text-white font-black leading-none"
                  style={{ fontSize: "6px", letterSpacing: "0.05em" }}
                >
                  {formatTimestamp(cp.timestamp)}
                </span>
              </div>
              <div className="flex flex-col px-2 py-1.5 flex-1 min-w-0 gap-1.5">
                <p className="text-[10px] font-semibold text-gray-700 leading-tight line-clamp-2">
                  {cp.question || "No question"}
                </p>
                <p className="text-[8px] text-orange-500 leading-tight line-clamp-1">
                  {cp.answer || "No answer"}
                </p>
                <span className="text-[6px] font-bold uppercase tracking-wide text-primary leading-none">
                  {gradingModeLabel[cp.gradingMode] ?? cp.gradingMode}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
